<template>
  <ChartLayout :title="title" :half="half">
    <DataGetter
      :url="apiRoute"
      :params="{
        countryISO,
        topic: 'Patents',
        indicator: 'Detail',
        startYear,
        endYear,
      }"
      v-slot="{ data }"
      class="patent-treemap"
      @fetched="setTotal"
      lazy
    >
      <p class="f-ui-2">
        Total:
        <strong>{{ formatNumber(total) }}</strong> energy-related patents
        between {{ startYear }} and {{ endYear }}.
      </p>
      <DataSlicer
        :data="data"
        x-axis-dimension="country"
        :y-axis-dimension="groupBy"
        value-dimension="value"
        v-slot="{ slicedData }"
      >
        <Chart
          :options="{
            ...chartOptions,
            series: [
              {
                data: [
                  { name: 'Clean Energy', id: 'Clean Energy' },
                  { name: 'Fossil fuel', id: 'Fossil Fuel' },
                  ...formatSeries(slicedData, data),
                ],
              },
            ],
          }"
        />
        <ChartLegend
          v-if="showLegend"
          :legend-items="formatSeries(slicedData)"
          :hover-series="() => {}"
        />
      </DataSlicer>
    </DataGetter>
    <template #notes>
      <p v-if="endYear >= provisionalYear">
      The datapoints for 2022 and 2023 are considered provisional due to the time lag in filing applications in the PATSTAT database.
        </p>
    </template>
  </ChartLayout>
</template>

<script>
import { Chart } from "highcharts-vue";
import ChartOptions, {
  ChartLayout,
  ChartLegend,
} from "@international-energy-agency/vue-ui/highcharts";
import { DataGetter, DataSlicer } from "@international-energy-agency/vue-ui";
export default {
  props: {
    title: {
      type: String,
      default: "",
    },
    apiRoute: {
      type: String,
      default: "https://api.iea.org/h2020/",
    },
    countryISO: {
      type: String,
      required: true,
    },
    groupBy: {
      type: String,
      default: "category",
    },
    startYear: {
      type: Number,
      default: 2000,
    },
    endYear: {
      type: Number,
      default: 2020,
    },
    provisionalYear: {
      type: Number,
      default: 2022
    },
    half: {
      type: Boolean,
      default: false,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    handleOnClick: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    Chart,
    ChartLayout,
    ChartLegend,
    DataSlicer,
    DataGetter,
  },
  data() {
    return {
      total: 0,
    };
  },
  computed: {
    chartOptions() {
      const { total, addBreaks, handleClick } = this;
      return ChartOptions.buildOptions(
        {
          chart: {
            height: 600,
            margin: 0,
          },
          plotOptions: {
            treemap: {
              levels: [
                {
                  level: 1,
                  dataLabels: {
                    enabled: true,
                    align: "center",
                    verticalAlign: "middle",
                    useHTML: true,
                    zIndex: 99,
                    className: "category-heading",
                  },
                },
                {
                  level: 2,
                  layoutAlgorithm: "squarified",
                  dataLabels: {
                    align: "left",
                    verticalAlign: "top",
                    allowOverlap: false,
                    useHTML: true,
                    zIndex: 99,
                    formatter: function () {
                      return `${addBreaks(this.point.name)}<br>(${(
                        (this.point.value / total) *
                        100
                      ).toFixed(1)}%)`;
                    },
                  },
                },
              ],

              events: {
                click: handleClick,
              },
            },
          },
          tooltip: {
            outside: true,
            zIndex: 999,
            valueDecimals: 0,
            valueSuffix: " patents",
          },
        },
        "treemap"
      );
    },
  },
  methods: {
    setTotal(data) {
      this.total = data.reduce((total, row) => total + row.value, 0);
    },
    formatSeries(slicedData, data) {
      return slicedData.map((series) => {
        const category = data.find(
          (row) => row.techSector === series.name
        ).category;
        return {
          name: series.name,
          parent: category === "Clean Energy" ? category : "Fossil Fuel",
          colorIndex: category === "Clean Energy" ? 2 : 5,
          value: series.data,
        };
      });
    },
    formatNumber(number) {
      return Math.floor(number).toLocaleString("fr");
    },
    addBreaks(text) {
      return (
        text
          // Avoid awkward breaks on ampersands
          .replace(" &", "&nbsp;&amp;")
          // Add non-breaking hyphens
          .replace("-", "&#x2011;")
      );
    },
    handleClick(e) {
      this.handleOnClick(e.point);
    },
  },
};
</script>

<style>
.highcharts-treemap-series {
  cursor: pointer;
}
.highcharts-treemap-series .highcharts-point {
  stroke: #000 !important;
  stroke-width: 0.5 !important;
}
.highcharts-treemap-series .highcharts-data-label text {
  font-weight: 400;
}
.highcharts-treemap-series .highcharts-label .highcharts-data-label {
  min-width: 80px;
}
.category-heading span {
  font-size: 48px;
  font-weight: 600;
  opacity: 0.33;
  text-align: center;
}
</style>
